import { BaseApiClient } from "../api/baseApiClient.service";

/**
 * Feedback category types
 */
export type FeedbackCategory =
	| "BUG"
	| "FEATURE_REQUEST"
	| "IMPROVEMENT"
	| "UX"
	| "PERFORMANCE"
	| "OTHER";

/**
 * Feedback status types
 */
export type FeedbackStatus =
	| "NEW"
	| "REVIEWED"
	| "IN_PROGRESS"
	| "RESOLVED"
	| "WONT_FIX";

/**
 * Request payload for creating feedback
 */
export interface CreateFeedbackRequest {
	title: string;
	description: string;
	category: FeedbackCategory;
	currentPage?: string;
	metadata?: Record<string, unknown>;
}

/**
 * Response from creating feedback
 */
export interface CreateFeedbackResponse {
	id: string;
	message: string;
}

/**
 * Feedback item response
 */
export interface FeedbackItem {
	id: string;
	createdAt: string;
	updatedAt: string;
	category: FeedbackCategory;
	title: string;
	description: string;
	status: FeedbackStatus;
	currentPage?: string;
}

/**
 * Feedback item with user info (for platform managers)
 */
export interface FeedbackItemWithUser extends FeedbackItem {
	user: {
		id: string;
		email: string;
		name: string | null;
	};
	userAgent?: string | null;
	metadata?: Record<string, unknown> | null;
}

/**
 * Service for managing user feedback submissions
 */
export class FeedbackService {
	private static instance: FeedbackService;
	private client: BaseApiClient;

	private constructor() {
		this.client = BaseApiClient.getInstance();
	}

	/**
	 * Get singleton instance
	 */
	public static getInstance(): FeedbackService {
		if (!FeedbackService.instance) {
			FeedbackService.instance = new FeedbackService();
		}
		return FeedbackService.instance;
	}

	/**
	 * Submit new feedback
	 * @param feedback - The feedback data to submit
	 * @returns Response with feedback ID and success message
	 */
	async submitFeedback(
		feedback: CreateFeedbackRequest,
	): Promise<CreateFeedbackResponse> {
		return await this.client.post<
			CreateFeedbackResponse,
			CreateFeedbackRequest
		>("feedback", feedback);
	}

	/**
	 * Get current user's feedback submissions
	 * @returns List of user's feedback items
	 */
	async getUserFeedback(): Promise<FeedbackItem[]> {
		return await this.client.get<FeedbackItem[]>("feedback/user");
	}

	/**
	 * Get all feedback (Platform Manager only)
	 * @returns List of all feedback items with user info
	 */
	async getAllFeedback(): Promise<FeedbackItemWithUser[]> {
		const response = await this.client.get<{
			success: boolean;
			feedback: FeedbackItemWithUser[];
		}>("platform-manager/feedback");
		return response.feedback;
	}

	/**
	 * Update feedback status (Platform Manager only)
	 * @param feedbackId - The feedback ID
	 * @param status - The new status
	 */
	async updateFeedbackStatus(
		feedbackId: string,
		status: FeedbackStatus,
	): Promise<void> {
		await this.client.patch<
			{ success: boolean; message: string },
			{ status: FeedbackStatus }
		>(`platform-manager/feedback/${feedbackId}/status`, { status });
	}

	/**
	 * Get category display name
	 */
	static getCategoryDisplayName(category: FeedbackCategory): string {
		const names: Record<FeedbackCategory, string> = {
			BUG: "Bug Report",
			FEATURE_REQUEST: "Feature Request",
			IMPROVEMENT: "Improvement Suggestion",
			UX: "User Experience",
			PERFORMANCE: "Performance Issue",
			OTHER: "General Feedback",
		};
		return names[category];
	}

	/**
	 * Get category icon emoji
	 */
	static getCategoryIcon(category: FeedbackCategory): string {
		const icons: Record<FeedbackCategory, string> = {
			BUG: "🐛",
			FEATURE_REQUEST: "💡",
			IMPROVEMENT: "✨",
			UX: "🎨",
			PERFORMANCE: "⚡",
			OTHER: "💬",
		};
		return icons[category];
	}

	/**
	 * Get status display name
	 */
	static getStatusDisplayName(status: FeedbackStatus): string {
		const names: Record<FeedbackStatus, string> = {
			NEW: "New",
			REVIEWED: "Under Review",
			IN_PROGRESS: "In Progress",
			RESOLVED: "Resolved",
			WONT_FIX: "Won't Fix",
		};
		return names[status];
	}

	/**
	 * Get status color class
	 */
	static getStatusColor(status: FeedbackStatus): string {
		const colors: Record<FeedbackStatus, string> = {
			NEW: "bg-blue-100 text-blue-800",
			REVIEWED: "bg-yellow-100 text-yellow-800",
			IN_PROGRESS: "bg-purple-100 text-purple-800",
			RESOLVED: "bg-green-100 text-green-800",
			WONT_FIX: "bg-gray-100 text-gray-800",
		};
		return colors[status];
	}
}
